import {fetchRemoteResult} from "@core/Api/api-util";
import ApiBase from "@core/Api/api-base";

const controllerEndpoint = `/AudienceManager/`;

const SERIALIZE_AUDIENCE_EDIT_MODEl = Symbol(); 


class CancellablePromise extends Promise {
    /**
     * Creates a new Promise.
     * @param executor
     * @param cancelHandler {function} Handle an external cancellation call
     */
    constructor(executor, cancelHandler) {
        super(executor);

        this.cancel = (function(){
            this.isCancelled = true;
            cancelHandler();
        }).bind(this);

        this.isCancelled = false;
    }
}

/**
 * Class used for consuming the AudienceManager API
 */
class AudienceManagerApi extends ApiBase {
    constructor() {
        super('/AudienceManager/');
    }

    /**
     *
     * @param {string} filterType   Selected filter type.
     * @param {string} search       Search text for given filter type.
     * @returns {Promise<Array.<{label: string, value: string}>>} Collection of dropdown values matching the given filterType + search value
     */
    getFilterOptions(filterType, search){
        const parameters = ApiBase.parameterize({filterType, search});
        return this.getResult(`GetFilterOptions?${parameters}`);
    }

    /**
     * Obtain progress for the given audience
     * @returns {CancellablePromise<AudienceProgress>}
     */
    getProgress(audienceId){
        const controller = new AbortController();
        
        return new CancellablePromise((resolve, reject) => {
            fetchRemoteResult(`${controllerEndpoint}GetAudienceProgress?audienceId=${audienceId}`, {
                signal: controller.signal
            }).then(resolve, reject);
        }, () => controller.abort());
    }
    
    /**
     * Gets all tabs with their categories and questions.
     * @returns {Promise<T>}
     */
    getTabs() {
        return this.getResult('GetQuestionsForTabs');
    }
    
    /**
     * Get a category by it's id.
     * @param {String} categoryId
     * @returns {Promise<T>}
     */
    getCategory(categoryId) {
        return this.getResult(`GetQuestionsForCategory/${categoryId}`);
    }

    /**
     * Lists all audiences in current user division.
     * @returns {Promise<T>}
     */
    listAudiences(includeAnswers) {
        const url = includeAnswers ? 
        `ListAudiences?includeAnswers=${includeAnswers}` : 
        'ListAudiences';

        return this.getResult(url);
    }

    /**
     * Lists all channels available for audiences.
     * @returns {Promise<T>}
     */
    listChannels() {
        return this.getResult(`ListChannels`);
    }

    /**
     * Set the channels of audience with given id.
     * @param {String} id The audience id.
     * @param {Array} channels The channels to set.
     * @returns {Promise<T>}
     */
    setChannels(id, channels) {
        return this.postAndFetchJson(`SetChannels`, { 

            body: JSON.stringify({
                AudienceId: id,
                Channels: channels
            })
         });
    }

    /**
     * Set the status of audience with given id.
     * @param {String} id The audience id. 
     * @param {Number} status The status to set.
     * @returns {Promise<T>}
     */
    setStatus(id, status) {
        return this.postAndFetchJson(`ChangeStatus`, { 

            body: JSON.stringify({
                AudienceId: id,
                Status: status
            })
         });
    }

    /**
     * Delete an audience with given id.
     * @param {string} id
     * @returns {Promise<T>}
     */
    deleteAudience(id) {
        return this.postAndFetchJson(`Delete/${id}`);
    }

    /**
     * Obtain circle chart data for the given audience.
     *
     * @param {string} audienceId Id of the audience for which to retrieve circle chart data.
     * @returns {Promise<{ rootNode: object, metadata: object }>}
     */
    getAudienceCircleChartData(audienceId){
        return this.getResult(`GetCircleChartConfiguration/${audienceId}`);
    }

    /**
     * Get the audience associated with the given id for the audience builder
     * @param {String} id Id of the audience to edit
     * @return {Promise<AudienceEditModel>}
     */
    getBuilderAudience(id){
        return this.getResult(`Builder/${id}/Audience`);
    }

    /**
     * Create an audience based on the given model
     * @param {String} audienceId Id to use for the new audience
     * @param {AudienceEditModel} model
     * @return {Promise<void>}
     */
    async createAudience(audienceId, model){
        return this.postResult(`create/${audienceId}`, {
            body: this[SERIALIZE_AUDIENCE_EDIT_MODEl](model),
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }

    /**
     * Create an audience with the given id based on the given CSV file
     * @param {String} audienceId Id to use for the new audience
     * @param {File} csvFile CSV file to base the audience on
     * @param {String} [name] Name to use for the audience, when no name is provided; a default name will be used
     * @return {Promise<AudienceEditModel>}
     */
    async createAudienceFromCsv(audienceId, csvFile, name){
        const form = new FormData();

        form.append('file', csvFile);
        if(name) form.append('name', name);

        return this.postResult(`Builder/${audienceId}/Upload`, {
            body: form
        });
    }

    /**
     * Update an audience based on the given model
     * @param {String} audienceId Id of the audience to update
     * @param {AudienceEditModel} model
     * @return {Promise<void>}
     */
    async updateAudience(audienceId, model){
        return this.putResult(`edit/${audienceId}`, {
            body: this[SERIALIZE_AUDIENCE_EDIT_MODEl](model),
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }

    /**
     * Serializes the given model to JSON for consumption by our API
     * @param {AudienceEditModel} model
     * @return {String} JSON stringified model
     */
    [SERIALIZE_AUDIENCE_EDIT_MODEl](model){
        //The model is not owned by us; thus we'll need to modify a copy
        model = {
            ...model, 
            answers: model.answers.map(answer => {
                //MVC can't handle JObjects :(
                return {
                    ...answer,
                    metadata: JSON.stringify(answer.metadata)
                };
            })
        };
        
        return JSON.stringify(model);
    }
}

export default new AudienceManagerApi();